import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import deepEqual from 'deep-equal'
import { getId as generateUuid } from '@adalo/utils'
import { toast } from 'react-toastify'
import Tooltip from 'components/Shared/Tooltip'
import Icon, { IconButton } from 'components/Shared/Icon'

import { newAction } from 'utils/actions'
import {
  copyActionChain,
  hasActionClipboard,
  pasteActionChain,
  remapActionChain,
  subscribe,
} from 'utils/actionClipboard'

import { getUpgraded, getDatasourceType, getTrialInformation } from 'ducks/apps'
import { getDatasources } from 'ducks/apps/datasources'
import { openAccordion } from 'ducks/accordions'
import { getActions, updateObject } from 'ducks/editor/objects'

import Button from 'components/Shared/Button'

import Item from './Item'
import EmptyState from './EmptyState'

class ActionRow extends Component {
  static propTypes = {
    appId: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    object: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    actions: PropTypes.objectOf(PropTypes.shape({})),
    componentId: PropTypes.string.isRequired,
    datasourceType: PropTypes.string.isRequired,
    updateObject: PropTypes.func.isRequired,
    openAccordion: PropTypes.func.isRequired,
  }

  state = {
    hasClipboard: hasActionClipboard(),
  }

  componentDidMount() {
    this.unsubscribe = subscribe(() => {
      this.setState({ hasClipboard: hasActionClipboard() })
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }

  handleCopy = () => {
    const { actions, appId, targetDatasources } = this.props
    const validKeys = Object.keys(actions).filter(id => actions[id])

    if (validKeys.length === 0) return

    const action = actions[validKeys[0]]

    copyActionChain(action, appId, targetDatasources)
    toast.success('Actions copied', {
      position: toast.POSITION.BOTTOM_LEFT,
    })
  }

  handlePaste = () => {
    const {
      actions,
      updateObject,
      object,
      openAccordion,
      appId,
      targetDatasources,
    } = this.props
    const result = pasteActionChain()

    if (!result) return

    let { action: pastedAction, sourceAppId, sourceDatasources } = result

    // Remap references when pasting across apps
    if (sourceAppId && sourceAppId !== appId && sourceDatasources) {
      pastedAction = remapActionChain(
        pastedAction,
        sourceDatasources,
        targetDatasources
      )
    }

    const actionKeys = Object.keys(actions)

    // If there are existing actions, merge into the first chain
    if (actionKeys.length > 0) {
      const existingAction = actions[actionKeys[0]]

      if (existingAction) {
        const merged = {
          ...existingAction,
          actions: [...(existingAction.actions || []), ...pastedAction.actions],
        }

        openAccordion('action-item', pastedAction.actions[0].id)
        updateObject(object.id, {
          actions: { ...actions, [actionKeys[0]]: merged },
        })
        toast.success('Actions pasted', {
          position: toast.POSITION.BOTTOM_LEFT,
        })

        return
      }
    }

    // No existing actions — create a new entry
    const actionId = generateUuid()
    openAccordion('action-item', pastedAction.actions[0].id)
    updateObject(object.id, {
      actions: { ...actions, [actionId]: pastedAction },
    })
    toast.success('Actions pasted', {
      position: toast.POSITION.BOTTOM_LEFT,
    })
  }

  handleCreate = defaults => {
    const { openAccordion } = this.props

    const actionItem = newAction(defaults)

    openAccordion('action-item', actionItem.actions[0].id)

    this.handleUpdate(generateUuid())(actionItem)
  }

  handleUpdate = id => action => {
    const { actions, updateObject, object } = this.props

    if (!action.actions || action.actions.length === 0) {
      this.handleRemove(id)()
    } else if (!deepEqual(actions[id], action)) {
      const newActions = {
        ...actions,
        [id]: action,
      }
      updateObject(object.id, { actions: newActions })
    }
  }

  handleRemove = id => () => {
    const { updateObject, object } = this.props
    let { actions } = this.props

    actions = {
      ...actions,
      [id]: undefined,
    }

    updateObject(object.id, { actions })
  }

  render() {
    const {
      actions,
      appId,
      componentId,
      displayName,
      object,
      datasourceType,
      isAfterTrial,
    } = this.props

    const actionKeys = Object.keys(actions)
    const { hasClipboard } = this.state

    const tooltip = (
      <a
        href="https://help.adalo.com/action-basics"
        target="_blank"
        rel="noreferrer"
      >
        Learn more.
      </a>
    )

    return (
      <div
        className={
          isAfterTrial
            ? 'library-inspect-action after-trial-action'
            : 'library-inspect-action'
        }
      >
        <div className="action-row-header">
          <p className="library-inspect-action-title">{displayName}</p>
          <div className="action-row-header-icons">
            {actionKeys.length > 0 && (
              <IconButton
                type="copy"
                small
                tooltip="Copy Actions"
                onClick={this.handleCopy}
              />
            )}
            <Tooltip tooltip={tooltip} hideArrow small>
              <Icon type="help-small" />
            </Tooltip>
          </div>
        </div>
        {actionKeys.length === 0 ? (
          <>
            <EmptyState
              onCreate={this.handleCreate}
              appId={appId}
              componentId={componentId}
              datasourceType={datasourceType}
              objectId={object?.id}
            >
              <Button icon="plus-small" listAddButton gray>
                Add Action
              </Button>
            </EmptyState>
            {hasClipboard && (
              <Button
                type="button"
                icon="copy"
                listAddButton
                gray
                onClick={this.handlePaste}
              >
                Paste Actions
              </Button>
            )}
          </>
        ) : (
          <>
            {actionKeys.map(id => (
              <Item
                key={id}
                id={id}
                action={actions[id]}
                onRemove={this.handleRemove(id)}
                onUpdate={this.handleUpdate(id)}
                appId={appId}
                componentId={componentId}
                objectId={object.id}
                datasourceType={datasourceType}
              />
            ))}
            {hasClipboard && (
              <Button
                type="button"
                icon="copy"
                listAddButton
                gray
                onClick={this.handlePaste}
              >
                Paste Actions
              </Button>
            )}
          </>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state, { appId, object }) => {
  let actions = {}

  const { trialState } = getTrialInformation(state, appId)
  const paying = getUpgraded(state, appId)

  const isAfterTrial =
    !paying && (trialState === 'after' || trialState === 'before')

  if (object) {
    actions = getActions(state, object.id)
  }

  return {
    actions,
    datasourceType: getDatasourceType(state, appId),
    isAfterTrial,
    targetDatasources: getDatasources(state, appId),
  }
}

export default connect(mapStateToProps, { updateObject, openAccordion })(
  ActionRow
)
